import React, { useCallback, useMemo } from "react"
import { Box, MenuItem, TextField, Typography } from "@mui/material"
import { blueGrey } from "@mui/material/colors"
import DataGridCellExpand from "components/DataGridCellExpand/DataGridCellExpand"
import { menuNames, modeNames, MODE_NONE, MODE_READ, MODE_EDIT } from "./constant"

/**
 * Page-permission editing grid, shared by PermissionGroupDialog (group perms)
 * and AccountDialog override dialog (withInherit + effective from groups).
 *
 * pages: [{PageCd, Name, MenuNum, NumStr}] from /permission-group/getPages
 * values: { PageCd: Mode } — for withInherit, a missing key means "inherit"
 * effective: { PageCd: Mode } effective perms from groups (withInherit only)
 */
const INHERIT = -1

const PagePermissionGrid = ({ pages, values, setValues, withInherit, effective, height = 450 }) => {

  const rows = useMemo(() => {
    const result = []
    let lastMenuNum = null
    for (const p of pages) {
      if (p.MenuNum !== lastMenuNum) {
        lastMenuNum = p.MenuNum
        result.push({ id: `HEADER-${p.MenuNum}`, IsHeader: true, MenuNum: p.MenuNum, Name: menuNames[p.MenuNum] || `เมนู ${p.MenuNum}` })
      }
      result.push({ id: p.PageCd, PageCd: p.PageCd, Name: p.Name, NumStr: p.NumStr })
    }
    return result
  }, [pages])

  const handleModeChange = useCallback((pageCd) => (e) => {
    const value = +e.target.value
    setValues(o => {
      const result = { ...o }
      if (withInherit && value === INHERIT) delete result[pageCd]
      else result[pageCd] = value
      return result
    })
  }, [setValues, withInherit])

  // header-row select: apply one mode to every page in the category
  const handleCategoryModeChange = useCallback((menuNum) => (e) => {
    const value = +e.target.value
    const pageCds = pages.filter(p => p.MenuNum === menuNum).map(p => p.PageCd)
    setValues(o => {
      const result = { ...o }
      for (const pageCd of pageCds) {
        if (withInherit && value === INHERIT) delete result[pageCd]
        else result[pageCd] = value
      }
      return result
    })
  }, [pages, setValues, withInherit])

  // common mode of a category, or "" (shown blank) when pages differ
  const categoryValue = useCallback((menuNum) => {
    const modes = pages.filter(p => p.MenuNum === menuNum).map(p => withInherit
      ? (values[p.PageCd] == null ? INHERIT : values[p.PageCd])
      : (values[p.PageCd] || MODE_NONE))
    return modes.length > 0 && modes.every(m => m === modes[0]) ? modes[0] : ""
  }, [pages, values, withInherit])

  const columns = useMemo(() => [
    {
      field: "NumStr", headerName: "เมนู", width: 60, sortable: false,
    },
    {
      field: "Name", headerName: "หน้า", flex: 1, sortable: false,
      renderCell: (params) => params.row.IsHeader
        ? <Typography fontWeight="bold" color="primary">{params.row.Name}</Typography>
        : params.row.Name
    },
    ...(withInherit ? [{
      field: "Effective", headerName: "สิทธิ์จากกลุ่ม", width: 110, sortable: false,
      renderCell: (params) => params.row.IsHeader ? null
        : modeNames[(effective && effective[params.row.PageCd]) || MODE_NONE]
    }] : []),
    {
      field: "Mode", headerName: withInherit ? "กำหนดเอง" : "สิทธิ์", width: 140, sortable: false,
      renderCell: (params) => {
        const isHeader = params.row.IsHeader
        const value = isHeader
          ? categoryValue(params.row.MenuNum)
          : withInherit
            ? (values[params.row.PageCd] == null ? INHERIT : values[params.row.PageCd])
            : (values[params.row.PageCd] || MODE_NONE)
        return (
          <TextField select fullWidth size="small" variant="standard"
            value={value}
            onChange={isHeader ? handleCategoryModeChange(params.row.MenuNum) : handleModeChange(params.row.PageCd)}
            sx={isHeader ? { "& .MuiSelect-select": { fontWeight: "bold" } } : null}>
            {withInherit && <MenuItem value={INHERIT}>ตามกลุ่ม</MenuItem>}
            <MenuItem value={MODE_NONE}>{modeNames[MODE_NONE]}</MenuItem>
            <MenuItem value={MODE_READ}>{modeNames[MODE_READ]}</MenuItem>
            <MenuItem value={MODE_EDIT}>{modeNames[MODE_EDIT]}</MenuItem>
          </TextField>
        )
      }
    },
  ], [values, handleModeChange, handleCategoryModeChange, categoryValue, withInherit, effective])

  return (
    <Box height={height} sx={{
      "& .header-row": { bgcolor: blueGrey[50] },
    }}>
      <DataGridCellExpand
        hideToolbar
        hideFooter
        rowHeight={38}
        rows={rows}
        columns={columns}
        getRowClassName={(params) => params.row.IsHeader ? "header-row" : ""}
      />
    </Box>
  )
}

export default React.memo(PagePermissionGrid)
